/**
 *  @swagger
 *  definitions:
 *    AdminSocialUserParams:
 *      type: object
 *      properties:
 *        AdminId:
 *          description: ID of the associated Admin
 *          type: string
 *        Type:
 *          description: Type of the Admin
 *          $ref: '#/components/enums/AdminType'
 *        Uid:
 *          description: Uid of the AdminSocialUser
 *          type: string
 *        Name:
 *          description: Name of the AdminSocialUser
 *          type: string
 *        Email:
 *          description: Email of the AdminSocialUser
 *          type: string
 *        RemoteImage:
 *          description: Rrmote Image of the AdminSocialUser
 *          type: string
 *          nullable: true
 *
 *  @swagger
 *  definitions:
 *    AdminSocialUserCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/AdminSocialUserParams'
 *        - type: object
 *          required:
 *            - AdminId
 *            - Type
 *            - Uid
 *            - Name
 *            - Email
 *
 *  @swagger
 *  definitions:
 *    AdminSocialUserQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the AdminSocialUser
 *              type: string
 *        - $ref: '#/definitions/AdminSocialUserParams'
 *
 *  @swagger
 *  definitions:
 *    AdminSocialUserUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/AdminSocialUserQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    AdminSocialUser:
 *      allOf:
 *        - $ref: '#/definitions/AdminSocialUserCreateParams'
 *        - $ref: '#/definitions/AdminSocialUserUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      AdminSocialUser:
 *        allOf:
 *          - $ref: '#/definitions/AdminSocialUser'
 *          - type: object
 *            properties:
 *              Admin:
 *                description: Admin of the AdminSocialUser
 *                $ref: '#/components/schemas/Admin'
 */

// Enums
import {
  AdminSocialUserType,
} from '../enums';

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $RelationIds = {
  AdminId: string;
};

export type $Params = {
  Email: string;
  Name: string;
  RemoteImage: null | string;
  Type: AdminSocialUserType;
  Uid: string;
};

export type $CreateParams = $Params & $RelationIds;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$CreateParams>;

export type $Associations = {
  Admin?: $Model<'Admin'>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  Email: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      isEmail: true,
      notEmpty: true,
    },
  },
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  Name: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  RemoteImage: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Type: {
    allowNull: false,
    type: Sequelize.ENUM(...Object.values(AdminSocialUserType)),
    validate: {
      notEmpty: true,
    },
  },
  Uid: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'AdminSocialUser'> => {
  const AdminSocialUser = stack.define(
    'AdminSocialUser',
    fields(Sequelize),
    {
      tableName: 'AdminSocialUsers',
    },
  );

  AdminSocialUser.associate = (models: $ModelClasses) => {
    models.AdminSocialUser.belongsTo(
      models.Admin,
      {
        as: 'Admin',
        foreignKey: 'AdminId',
      },
    );
  };

  AdminSocialUser.addScope(
    'defaultScope',
    {
      order: [['createdAt', 'ASC']],
    },
  );

  return AdminSocialUser;
});
